import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Text,
  Button,
  Box,
  Link,
} from '@chakra-ui/react'
import { VerifyFormInput } from '../atoms/VerifyFormInput'
import { FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation } from '@tanstack/react-query'

const schema = yup.object({
  round: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .required('The field is required')
    .min(1, "The value can't be less than 1"),
})

export const VerifyAutofillModal = ({
  mode,
  onConfirm,
  onClose,
}: {
  mode: 'drand' | 'drng'
  onClose: () => void
  onConfirm: (block: number, signature: string) => void
}) => {
  const form = useForm({
    resolver: yupResolver(schema),
  })

  const urlFetch =
    mode == 'drand'
      ? 'https://drand.cloudflare.com/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/public/'
      : 'https://app-mainnet.blaze.ninja/api-game/block/'

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      await form.trigger()
      if (!form.formState.isValid) {
        return
      }
      const round = form.getValues('round')
      const response = await fetch(urlFetch + round)

      if (response.status == 404) {
        if (mode == 'drng') {
          const data = await response.json()
          form.setError('round', {
            type: 'manual',
            message: 'Round not found - actually Injective block' + data.currentHeight,
          })
          return
        }

        const timeRound = new Date((1692803367 + round * 3) * 1000)
        form.setError('round', {
          type: 'manual',
          message: 'Round not found:' + timeRound.toLocaleString(),
        })
        return
      }
      const data = await response.json()
      const { signature } = data

      onClose()
      onConfirm(round, signature)
    },
  })

  return (
    <Modal size={'sm'} isCentered isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent overflow={'hidden'} pos={'relative'} marginLeft={2.5} marginRight={2.5}>
        <ModalCloseButton zIndex={10} right={6} top={6} onClick={onClose} size="sm" />
        <ModalBody p={0}>
          <Text
            borderBottom={'1px solid'}
            borderColor={'ninja.500'}
            textAlign={'center'}
            fontSize={'xl'}
            fontWeight={700}
            py={6}
          >
            Auto fill
          </Text>
          <Box p={6}>
            <Box mb={5}>
              <FormProvider {...form}>
                <VerifyFormInput
                  label={mode === 'drng' ? 'Injective Block' : 'Drand Round'}
                  placeholder={mode === 'drng' ? 'Enter Injective Block' : 'Enter Drand Round'}
                  name="round"
                  type="number"
                />
              </FormProvider>
            </Box>
            <Button h={'56px'} rounded={'2xl'} w={'full'} isLoading={isPending} onClick={() => mutate()}>
              Auto fill
            </Button>
            <Text fontSize={'xs'} fontWeight={700} mt={1}>
              You can manually fetch the {mode === 'drng' ? 'block' : 'round'} from: <br />
              <Link
                href={urlFetch + (form.watch('round') || '{round}')}
                target="_blank"
                color={'ninja.200'}
                _hover={{
                  color: 'ninja.300',
                  textDecoration: 'underline',
                }}
              >
                {urlFetch + (form.watch('round') || (mode === 'drng' ? '{block}' : '{round}'))}
              </Link>
            </Text>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
